
import React, { useState } from 'react';
import { IconSettings, IconUsers, IconHierarchy, IconCpu, IconSparkles } from '../components/Icons';
import { processAdminCommand } from '../services/geminiService';
import toast from 'react-hot-toast';

const MOCK_USERS = [
    { id: 'U1', name: 'John Doe', role: 'Trader', desk: 'FX Options', status: 'Active' },
    { id: 'U2', name: 'Alice Smith', role: 'Risk Manager', desk: 'Global Risk', status: 'Active' },
    { id: 'U3', name: 'Bob Jones', role: 'Ops Clerk', desk: 'Settlements', status: 'Locked' },
];

const MOCK_HIERARCHY = [
    { name: 'Global Markets', children: [
        { name: 'FX', children: ['Spot', 'Options', 'Swaps'] },
        { name: 'Rates', children: ['Govt Bonds', 'Swaps'] },
        { name: 'Equities', children: ['Cash', 'Derivatives'] }
    ]}
];

const AdminView: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'users' | 'hierarchy' | 'system'>('users');
    const [command, setCommand] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [users, setUsers] = useState(MOCK_USERS);

    const handleCommand = async () => {
        if (!command.trim()) return;
        setIsProcessing(true);
        
        // Simulate AI Delay
        await new Promise(r => setTimeout(r, 1000));
        
        const result = await processAdminCommand(command);
        
        if (result.action === 'CREATE_USER') {
            const newUser = {
                id: `U${users.length + 1}`,
                name: result.details.name || 'New User',
                role: result.details.role || 'Trader',
                desk: result.details.desk || 'General',
                status: 'Active'
            };
            setUsers([...users, newUser]);
            toast.success(result.confirmation);
        } else {
            toast(result.confirmation, { icon: '🔧' });
        }
        
        setCommand('');
        setIsProcessing(false);
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Sidebar Tabs */}
            <div className="w-64 bg-white border-r border-slate-200 flex flex-col">
                <div className="p-6 border-b border-slate-100">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                        <IconSettings /> Admin Console
                    </h2>
                    <p className="text-xs text-slate-500">System Configuration & Ref Data</p>
                </div>
                <nav className="flex-1 p-2 space-y-1">
                    <button 
                        onClick={() => setActiveTab('users')}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'users' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <IconUsers /><span className="ml-3">User Management</span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('hierarchy')}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'hierarchy' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <IconHierarchy /><span className="ml-3">Book Structure</span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('system')}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'system' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'}`}
                    >
                        <IconCpu /><span className="ml-3">System Health</span>
                    </button>
                </nav>
            </div>

            {/* Center: Content Area */}
            <div className="flex-1 p-8 overflow-y-auto custom-scrollbar">
                
                {/* AI Command Bar */}
                <div className="bg-white p-1 rounded-xl shadow-sm border border-slate-200 flex items-center mb-8">
                    <div className="pl-4 pr-2 text-purple-500"><IconSparkles /></div>
                    <input 
                        type="text" 
                        placeholder="AiQ Admin: Type a command (e.g., 'Create a new Risk Manager user named Sarah')"
                        className="flex-1 py-3 outline-none text-sm"
                        value={command}
                        onChange={(e) => setCommand(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleCommand()}
                    />
                    <button 
                        onClick={handleCommand}
                        disabled={isProcessing}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-purple-700 transition-colors disabled:opacity-50"
                    >
                        {isProcessing ? 'Processing...' : 'Execute'}
                    </button>
                </div>

                {activeTab === 'users' && (
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-slate-50 text-slate-500 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3">Name</th>
                                    <th className="px-6 py-3">Role</th>
                                    <th className="px-6 py-3">Desk</th>
                                    <th className="px-6 py-3">Status</th>
                                    <th className="px-6 py-3">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {users.map(u => (
                                    <tr key={u.id} className="hover:bg-slate-50">
                                        <td className="px-6 py-4 font-medium text-slate-900">{u.name}</td>
                                        <td className="px-6 py-4 text-slate-600"><span className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">{u.role}</span></td>
                                        <td className="px-6 py-4 text-slate-600">{u.desk}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 rounded text-xs font-bold ${u.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {u.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-blue-600 hover:underline cursor-pointer text-xs font-bold">Edit</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {activeTab === 'hierarchy' && (
                    <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
                        {MOCK_HIERARCHY.map((root) => (
                            <div key={root.name}>
                                <div className="font-bold text-lg text-slate-800 mb-4 flex items-center gap-2"><IconHierarchy /> {root.name}</div>
                                <div className="grid grid-cols-3 gap-6">
                                    {root.children.map((desk: any) => (
                                        <div key={desk.name} className="border border-slate-200 rounded-lg p-4">
                                            <div className="font-bold text-slate-700 mb-2 border-b border-slate-100 pb-2">{desk.name} Desk</div>
                                            <ul className="space-y-1">
                                                {desk.children.map((book: string) => (
                                                    <li key={book} className="text-xs text-slate-500 flex items-center gap-2">
                                                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                                        {book} Book
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeTab === 'system' && (
                    <div className="grid grid-cols-3 gap-6">
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-500 uppercase mb-2">API Status</h3>
                            <div className="text-2xl font-bold text-emerald-600">Online</div>
                            <p className="text-xs text-slate-400 mt-1">Latency: 45ms</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-500 uppercase mb-2">Database</h3>
                            <div className="text-2xl font-bold text-blue-600">Connected</div>
                            <p className="text-xs text-slate-400 mt-1">Trades: 14,205 records</p>
                        </div>
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
                            <h3 className="text-xs font-bold text-slate-500 uppercase mb-2">AI Tokens</h3>
                            <div className="text-2xl font-bold text-purple-600">45% Used</div>
                            <div className="w-full bg-slate-100 h-1.5 mt-2 rounded-full overflow-hidden">
                                <div className="bg-purple-500 h-full" style={{ width: '45%' }}></div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminView;
